import { AfterViewInit, Component, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { catchError, map, Observable, of, throwError } from 'rxjs';
import { SpinnerOverlayComponent } from 'src/app/components/spinner-overlay/spinner-overlay.component';
import { TabellaDinamica } from 'src/app/components/tabella-cfg-dinamica/tabella-cfg-dinamica.component';
import { Ambito } from 'src/app/services/ambiti/ambiti.service';
import { BilancioService } from 'src/app/services/bilancio/bilancio.service';
import { PianoService } from 'src/app/services/piano/piano.service';
import { PolicyService } from 'src/app/services/policy/policy.service';
import { ReportVsmeService } from 'src/app/services/report-vsme/report-vsme.service';
import { SintesiService } from 'src/app/services/sintesi/sintesi.service';
import { OpzioneTematica, Tematica } from 'src/app/services/tematiche/tematiche.service';
import { UtilityService } from 'src/app/services/utility/utility.service';

// ─── Types ───────────────────────────────────────────────────────────────────

/** Struttura base comune a tutte le risposte VSME. */
interface RispostaBase {
  idElemento: string;
}

/** Risposta per elementi di tipo KPI, include unità di misura e valori. */
interface RispostaKpi extends RispostaBase {
  idUnitaDiRiferimento: string;
  idCategoriaUnitaDiMisura: string;
  risposta: any[];
}

/** Motivazione per la quale la risposta non è stata tratta */
interface MotivazioneRisposta extends RispostaBase {
  infoNonDisponibile?: {
    checked: false;
    risposta: string;
  };
  infoNonRilevante?: {
    checked: false;
    risposta: string;
  };
}

/** Risposta per elementi di tipo azione o checkbox. */
interface RispostaAzione extends RispostaBase {
  risposta: string;
}

/** Risposta per elementi di tipo tabella dinamica. */
interface RispostaDynamicTable extends RispostaBase {
  risposta: any;
  colonne: any[];
  righe: any[];
  totaleRighe: boolean;
  totaleColonne: boolean;
  titoloTabella: string;
}

/** Unione discriminata di tutti i tipi di risposta VSME possibili. */
type RispostaVsme = RispostaKpi | MotivazioneRisposta | RispostaAzione | RispostaDynamicTable;

/** Dati iniettati nella dialog tramite {@link MAT_DIALOG_DATA}. */
export interface DialogCompilaSintesiData {
  idSintesiSostenibilita: string;
  idBilancioSostenibilita: string;
  idPolicySostenibilita: string;
  idPianoSostenibilita: string;
  idReportVsme: string;
  idAmbito: string;
  idTematica: string;
  idElemento: string;
  valueToggleAnni: boolean;
  idObiettivo: string;
  isPermessoSoloRead?: boolean;
  tipo?: 'Kpi' | 'AzioniEBuonePratiche' | 'Impegno' | 'Impatto' | 'Certificazioni';
  chiaveRisposta?: string;
}

/**
 * Oggetto di chiamata intermedio costruito prima di invocare
 * il servizio di salvataggio. Raccoglie tutti i campi necessari
 * indipendentemente dal tipo di contesto (sintesi, bilancio, ecc.).
 */
interface ObjCall {
  idAmbito: string;
  idTematica: string;
  idElemento: string;
  idUnitaDiRiferimento: string;
  idCategoriaUnitaDiMisura: string;
  risposta: any;
  rispostaBreve: string;
  anno: string;
  colonne?: any[];
  righe?: any[];
  totaleRighe?: boolean;
  totaleColonne?: boolean;
  titoloTabella?: string;
  infoNonDisponibile?: {
    checked: false;
    risposta: string;
  };
  infoNonRilevante?: {
    checked: false;
    risposta: string;
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Dialog per la compilazione di un elemento di sostenibilità
 * (KPI, azione, impegno, impatto, certificazione) nei diversi contesti:
 * Sintesi, Bilancio, Policy, Piano di Sostenibilità e Report VSME.
 *
 * Il contesto attivo è determinato dall'ID presente in {@link DialogCompilaSintesiData}.
 */
@Component({
  selector: 'app-dialog-compila-sintesi-sostenibilita',
  templateUrl: './dialog-compila-sintesi-sostenibilita.component.html',
  styleUrls: ['./dialog-compila-sintesi-sostenibilita.component.scss'],
  standalone: false,
})
export class DialogCompilaSintesiSostenibilitaComponent implements AfterViewInit {
  @ViewChild(SpinnerOverlayComponent) spinnerOver!: SpinnerOverlayComponent;

  /** Dati dell'elemento da compilare, caricati dal servizio. */
  public compilazione: any;
  /** Anno di esercizio dell'elemento. */
  public esercizio!: number;
  /** ID del contesto attivo (sintesi, bilancio, policy, ecc.). */
  public idContesto: string = '';
  /** Titolo del contesto, mostrato nell'intestazione della dialog. */
  public titolo: string = '';
  /** Risposta principale salvata al termine della compilazione. */
  public risposta: any;
  /** Risposta breve (solo per azioni e policy). */
  public rispostaBreve: any;
  /** Anno di riferimento (solo per azioni e piano). */
  public anno: string = '';

  constructor(
    public dialogRef: MatDialogRef<DialogCompilaSintesiSostenibilitaComponent>,
    private readonly sintesiService: SintesiService,
    private readonly bilancioService: BilancioService,
    private readonly policyService: PolicyService,
    private readonly pianoService: PianoService,
    private readonly reportVsmeService: ReportVsmeService,
    private readonly utilityService: UtilityService,
    @Inject(MAT_DIALOG_DATA) public data: DialogCompilaSintesiData,
  ) {}

  // ─── Lifecycle ─────────────────────────────────────────────────────────────

  /**
   * Avvia il caricamento dell'elemento da compilare dopo che la view
   * è stata inizializzata. Il `setTimeout` è necessario per evitare
   * l'errore `ExpressionChangedAfterItHasBeenChecked`.
   */
  ngAfterViewInit(): void {
    setTimeout(() => this._loadElementoDaCompilare(), 0);
  }

  // ─── Public API ────────────────────────────────────────────────────────────

  /**
   * Mostra o nasconde lo spinner di caricamento.
   * @param loading `true` per mostrare lo spinner, `false` per nasconderlo.
   */
  loading = (loading: boolean): void => {
    loading ? this.spinnerOver.show() : this.spinnerOver.hide();
  };

  /**
   * Chiude la dialog.
   * Se l'utente è in sola lettura o non ha ancora compilato nulla,
   * chiude senza restituire dati. Altrimenti restituisce la risposta salvata.
   */
  chiudi(): void {
    if (this.data?.isPermessoSoloRead || !this.risposta) {
      this.dialogRef.close();
      return;
    }
    this.dialogRef.close({ salvato: true, risposta: this.risposta });
  }

  /**
   * Callback invocata dal componente figlio al termine della compilazione.
   * Costruisce l'oggetto di chiamata, invoca il servizio corretto e
   * gestisce il risultato.
   *
   * @param compilazione Oggetto compilato restituito dal componente figlio.
   * @param isImportazione Se `true`, non chiude la dialog al termine del salvataggio.
   * @returns Observable del risultato della chiamata HTTP.
   */
  finitaCompilazione = (compilazione: any, isImportazione?: boolean): Observable<any> => {
    if (!compilazione) return of();

    const tematica: Tematica = this.compilazione.ambiti[0].tematiche[0];
    const ambito: Ambito = this.compilazione.ambiti[0];
    const objCall = this._buildObjCall(ambito, tematica);

    return this._postElementoDaCompilare(objCall).pipe(
      map(() => this._onSaveSuccess(objCall, isImportazione)),
      catchError((error) => {
        console.error(error);
        this.spinnerOver.hide();
        return this.data.idSintesiSostenibilita ? throwError(() => error) : of(error);
      }),
    );
  };

  // ─── Load ──────────────────────────────────────────────────────────────────

  /**
   * Mostra lo spinner, effettua la chiamata GET e popola `compilazione`.
   * In caso di errore, chiude la dialog e mostra uno snackbar.
   */
  private _loadElementoDaCompilare(): void {
    this.spinnerOver.show();
    this._getElementoDaCompilare().subscribe({
      next: (value) => {
        this.esercizio = value?.esercizio?.length ? value.esercizio[0] : 2024;
        this.compilazione = value;
      },
      error: (err) => {
        console.error(err);
        this.dialogRef.close();
        this.utilityService.openSnackBar('Errore caricamento', '', {
          duration: 2000,
          panelClass: ['red-snackbar'],
        });
      },
      complete: () => this.spinnerOver.hide(),
    });
  }

  /**
   * Seleziona il servizio corretto in base al contesto attivo e
   * restituisce l'Observable della chiamata GET.
   *
   * @returns Observable con i dati dell'elemento da compilare.
   */
  private _getElementoDaCompilare(): Observable<any> {
    const { idAmbito, idTematica, idElemento } = this.data;

    if (this.data.idSintesiSostenibilita) {
      this.titolo = 'Sintesi di Sostenibilità';
      this.idContesto = this.data.idSintesiSostenibilita;
      return this.sintesiService.getElementoDaCompilare(
        this.data.idSintesiSostenibilita,
        idAmbito,
        idTematica,
        idElemento,
      );
    }

    if (this.data.idBilancioSostenibilita) {
      this.titolo = 'Report di Sostenibilità';
      this.idContesto = this.data.idBilancioSostenibilita;
      return this.bilancioService.getElementoDaCompilare(
        this.data.idBilancioSostenibilita,
        idAmbito,
        idTematica,
        idElemento,
      );
    }

    if (this.data.idPolicySostenibilita) {
      this.titolo = 'Policy di Sostenibilità';
      this.idContesto = this.data.idPolicySostenibilita;
      return this.policyService.getElementoDaCompilare(
        this.data.idPolicySostenibilita,
        idAmbito,
        idTematica,
        idElemento,
      );
    }

    if (this.data.idPianoSostenibilita) {
      this.titolo = 'Piano di Sostenibilità';
      this.idContesto = this.data.idPianoSostenibilita;
      return this.pianoService.getElementoDaCompilare(
        this.data.idPianoSostenibilita,
        idAmbito,
        idTematica,
        idElemento,
        this.data.idObiettivo,
      );
    }

    if (this.data.idReportVsme) {
      this.titolo = 'Report Vsme';
      this.idContesto = this.data.idReportVsme;
      return this.reportVsmeService.getElementoDaCompilare(
        this.data.idReportVsme,
        idAmbito,
        idTematica,
        idElemento,
        this.data.chiaveRisposta ?? '',
      );
    }

    return of(null);
  }

  // ─── Save ──────────────────────────────────────────────────────────────────

  /**
   * Costruisce l'oggetto {@link ObjCall} partendo dai dati di ambito e tematica.
   * Per il contesto VSME delega a {@link _manageRispostaVsme},
   * altrimenti a {@link _buildRispostaTematica}.
   *
   * @param ambito Ambito corrente della compilazione.
   * @param tematica Tematica corrente della compilazione.
   * @returns Oggetto completo pronto per la chiamata POST.
   */
  private _buildObjCall(ambito: Ambito, tematica: Tematica): ObjCall {
    const base: ObjCall = {
      idAmbito: ambito.id,
      idTematica: tematica.id,
      idElemento: '',
      idUnitaDiRiferimento: '',
      idCategoriaUnitaDiMisura: '',
      risposta: '',
      rispostaBreve: '',
      anno: '',
    };

    if (this.data.idReportVsme) {
      const vsme = this._manageRispostaVsme(tematica);
      return { ...base, ...vsme, idElemento: vsme?.idElemento ?? '' };
    }

    return { ...base, ...this._buildRispostaTematica(tematica) };
  }

  /**
   * Ricava la risposta dalla tematica standard (non VSME),
   * gestendo in ordine: KPI, impegni, impatti, azioni, certificazioni.
   *
   * @param tematica Tematica da cui estrarre la risposta.
   * @returns Campi parziali da unire alla base di {@link ObjCall}.
   */
  private _buildRispostaTematica(tematica: Tematica): Partial<ObjCall> {
    if (tematica.kpis?.length) {
      const kpi = tematica.kpis[0];
      return {
        idElemento: kpi.id,
        idUnitaDiRiferimento: kpi.idUnitaDiRiferimento,
        idCategoriaUnitaDiMisura: kpi.idCategoriaUnitaDiMisura,
        risposta: [...(kpi.valoriKpi ?? []), ...(kpi.valoriTarget ?? [])],
      };
    }

    if (tematica.impegni?.length) return this._elementoToRisposta(tematica.impegni[0]);
    if (tematica.impatti?.length) return this._elementoToRisposta(tematica.impatti[0]);

    if (tematica.azioni?.length) {
      const azione = tematica.azioni[0];
      return {
        ...this._elementoToRisposta(azione),
        rispostaBreve: azione.rispostaBreve,
        anno: azione.anno,
      };
    }

    if (tematica.certificazioni?.length)
      return this._elementoToRisposta(tematica.certificazioni[0]);

    return {};
  }

  /**
   * Mappa un generico elemento (impegno, impatto, azione, certificazione)
   * nei campi `idElemento` e `risposta` di {@link ObjCall}.
   *
   * @param elemento Elemento da mappare.
   * @returns Campi parziali `idElemento` e `risposta`.
   */
  private _elementoToRisposta(elemento: any): Partial<ObjCall> {
    return {
      idElemento: elemento.id,
      risposta: elemento.risposta ?? elemento.valoreAttribuito ?? '',
    };
  }

  /**
   * Seleziona il servizio corretto e invoca la chiamata POST
   * in base al contesto attivo.
   *
   * @param objCall Oggetto con tutti i dati da inviare.
   * @returns Observable del risultato della chiamata HTTP.
   */
  private _postElementoDaCompilare(objCall: ObjCall): Observable<any> {
    const {
      idAmbito,
      idTematica,
      idElemento,
      risposta,
      rispostaBreve,
      anno,
      idCategoriaUnitaDiMisura,
      idUnitaDiRiferimento,
    } = objCall;

    if (this.data.idSintesiSostenibilita) {
      return this.sintesiService.postElementoDaCompilare(
        this.data.idSintesiSostenibilita,
        idAmbito,
        idTematica,
        idElemento,
        risposta,
        idCategoriaUnitaDiMisura,
        idUnitaDiRiferimento,
      );
    }

    if (this.data.idBilancioSostenibilita) {
      return this.bilancioService.postElementoDaCompilare(
        this.data.idBilancioSostenibilita,
        idAmbito,
        idTematica,
        idElemento,
        risposta,
        idCategoriaUnitaDiMisura,
        idUnitaDiRiferimento,
      );
    }

    if (this.data.idPolicySostenibilita) {
      return this.policyService.postElementoDaCompilare(
        this.data.idPolicySostenibilita,
        idAmbito,
        idTematica,
        idElemento,
        risposta,
        rispostaBreve,
        idCategoriaUnitaDiMisura,
        idUnitaDiRiferimento,
      );
    }

    if (this.data.idPianoSostenibilita) {
      return this.pianoService.postElementoDaCompilare(
        this.data.idPianoSostenibilita,
        idAmbito,
        idTematica,
        idElemento,
        risposta,
        rispostaBreve,
        anno,
        idCategoriaUnitaDiMisura,
        idUnitaDiRiferimento,
        this.data.idObiettivo,
      );
    }

    if (this.data.idReportVsme) {
      const dynamicTable: TabellaDinamica = {
        id: idElemento,
        colonne: objCall.colonne ?? [],
        righe: objCall.righe ?? [],
        totaleRighe: objCall.totaleRighe ?? false,
        totaleColonne: objCall.totaleColonne ?? false,
        titoloTabella: objCall.titoloTabella ?? '',
      };
      return this.reportVsmeService.postElementoDaCompilare(
        this.data.idReportVsme,
        idAmbito,
        idTematica,
        idElemento,
        risposta,
        idCategoriaUnitaDiMisura,
        idUnitaDiRiferimento,
        this.data.chiaveRisposta,
        dynamicTable,
        objCall.infoNonDisponibile,
        objCall.infoNonRilevante,
      );
    }

    return of(null);
  }

  /**
   * Aggiorna le proprietà pubbliche con i valori salvati e,
   * se non si tratta di un'importazione, chiude la dialog
   * restituendo i dati al chiamante.
   *
   * @param objCall Oggetto con i valori appena salvati.
   * @param isImportazione Se `true`, non chiude la dialog.
   */
  private _onSaveSuccess(objCall: ObjCall, isImportazione?: boolean): void {
    this.spinnerOver.hide();
    this.risposta = objCall.risposta;
    this.rispostaBreve = objCall.rispostaBreve;
    this.anno = objCall.anno;

    if (!isImportazione) {
      this.dialogRef.close({
        salvato: true,
        risposta: this.risposta,
        ...(objCall.rispostaBreve && { rispostaBreve: this.rispostaBreve }),
        ...(objCall.anno && { anno: this.anno }),
      });
    }
  }

  // ─── Vsme Builders ─────────────────────────────────────────────────────────

  /**
   * Punto di ingresso per la gestione della risposta VSME.
   * Legge la prima sotto-opzione disponibile e costruisce la risposta
   * in base al tipo e al flag `notRisposta`.
   *
   * @param tematica Tematica contenente le risposte VSME.
   * @returns Oggetto risposta VSME o `undefined` se non disponibile.
   */
  private _manageRispostaVsme(tematica: Tematica): RispostaVsme | undefined {
    const sottoOpzione = tematica.risposteVsme?.[0]?.sottoOpzioni?.[0];
    if (!sottoOpzione) return undefined;

    let { risposta, valoriKpi = [], valoriTarget = [] } = sottoOpzione;

    const {
      tipo,
      id,
      idUnitaDiRiferimento,
      idCategoriaUnitaDiMisura,
      infoNonDisponibile,
      infoNonRilevante,
      notRisposta,
    } = sottoOpzione;

    const idElemento = id ?? '';
    const motivazione = notRisposta ? { infoNonDisponibile, infoNonRilevante } : {};

    /** Se sono in motivazione allora pulisco le risposte degli elementi */
    if (notRisposta) {
      valoriKpi.forEach((v) => (v.valoreAttribuito = undefined));
      valoriTarget.forEach((v) => (v.valoreAttribuito = undefined));
      risposta = '';
    }

    switch (tipo) {
      case 'kpi':
        return {
          idElemento,
          idUnitaDiRiferimento,
          idCategoriaUnitaDiMisura,
          risposta: [...valoriKpi, ...valoriTarget],
          ...motivazione,
        };
      case 'azione':
      case 'checkbox':
        return {
          idElemento,
          risposta: risposta,
          ...motivazione,
        };
      case 'dynamic_table':
        return { ...this._buildRispostaDynamicTable(sottoOpzione), ...motivazione };
      default:
        return undefined;
    }
  }

  /**
   * Costruisce un oggetto {@link RispostaDynamicTable} dalla sotto-opzione,
   * mappando direttamente colonne, righe e metadati della tabella.
   *
   * @param sottoOpzione Sotto-opzione di tipo `dynamic_table`.
   * @returns Oggetto risposta completo per la tabella dinamica.
   */
  private _buildRispostaDynamicTable(sottoOpzione: OpzioneTematica): RispostaDynamicTable {
    const { id, colonne, righe, totaleRighe, totaleColonne, titoloTabella } = sottoOpzione;
    //! Pulire le celle?
    return {
      idElemento: id ?? '',
      risposta: sottoOpzione.risposta,
      colonne,
      righe,
      totaleRighe,
      totaleColonne,
      titoloTabella,
    };
  }
}
